import { useEffect, useState } from "react";
import { runFullPipeline, type PipelineEvent, type PipelineStep } from "../lib/pipeline";
import { useTasks } from "../store/tasks";
import { useOps } from "../store/ops";
import { useRH } from "../store/rh";
import { PERSONAS } from "../data/org";
import { initials, monogramColor } from "../lib/monogram";

const STEP_LABEL: Record<PipelineStep, string> = {
  estimate:   "1. Estimar complexidade",
  suggest:    "2. Time analisa briefing",
  assign:     "3. Designar",
  discuss:    "4. Mesa-redonda 🪑 (debate antes de executar)",
  execute:    "5. Execução paralela",
  mediate:    "6. Marcio concilia (se houver divergência)",
  tiebreaker: "7. Trump bate o martelo",
  approve:    "8. Aprovação final",
  deliver:    "9. Entregue ✓"
};

const STATUS_ICON: Record<PipelineEvent["status"], string> = {
  running: "⏳",
  done:    "✓",
  skipped: "—",
  error:   "✗",
  waiting: "?"
};

export function PipelineRunner({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const task = useTasks(s => s.tasks.find(t => t.id === taskId));
  const workers = useOps(s => s.workers);
  const addApproval = useTasks(s => s.addApproval);
  const setStage = useTasks(s => s.setStage);

  const [events, setEvents] = useState<PipelineEvent[]>([]);
  const [running, setRunning] = useState(false);
  const [autoApprove, setAutoApprove] = useState(true);
  const [skipConsensus, setSkipConsensus] = useState(false);
  const [enableDiscussion, setEnableDiscussion] = useState(true);
  const [needsApproval, setNeedsApproval] = useState(false);

  const start = async () => {
    setEvents([]);
    setRunning(true);
    setNeedsApproval(false);
    try {
      await runFullPipeline(
        { taskId, autoApprove, skipConsensus, enableDiscussion },
        (e) => {
          setEvents(prev => [...prev, e]);
          if (e.step === "approve" && e.status === "waiting") setNeedsApproval(true);
        }
      );
    } finally {
      setRunning(false);
    }
  };

  const approveAndFinish = () => {
    addApproval(taskId, { stage: "client-approval", by: "marcio", decision: "approved", notes: "Aprovado no checkpoint" });
    setStage(taskId, "delivered");
    setEvents(prev => [...prev, { step: "approve", status: "done", text: "Aprovado manualmente" },
                                 { step: "deliver", status: "done", text: "✓ Entregue" }]);
    setNeedsApproval(false);
  };

  const rejectAndStop = () => {
    const note = prompt("Motivo da rejeição (pra rodar de novo melhor)?") ?? "";
    addApproval(taskId, { stage: "client-approval", by: "marcio", decision: "rejected", notes: note });
    setEvents(prev => [...prev, { step: "approve", status: "error", text: "Marcio rejeitou: " + note }]);
    setNeedsApproval(false);
  };

  if (!task) return null;

  const currentStep = events.length > 0 ? events[events.length - 1].step : null;
  const assignedActive = task.assignedTo.map(id => PERSONAS.find(p => p.id === id)).filter(Boolean) as typeof PERSONAS;

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-5xl max-h-[94vh] overflow-y-auto
                      shadow-[8px_8px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-red text-bone px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-mono text-xs">PIPELINE-AUTO</div>
            <h2 className="font-display font-black uppercase text-2xl leading-none truncate">
              🤖 Pipeline Automático
            </h2>
            <div className="font-serif italic text-bone/80 text-sm truncate">{task.title}</div>
          </div>
          <button onClick={onClose} className="btn bg-ink text-bone border-ink shrink-0">fechar ×</button>
        </header>

        <div className="p-5 space-y-4">
          {/* CONTROLES */}
          {!running && events.length === 0 && (
            <section className="border-2 border-ink p-4 bg-bone">
              <div className="font-display font-bold uppercase text-sm mb-3">▸ configurações do pipeline</div>
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input type="checkbox" checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} />
                <span className="font-mono text-[13px]">Aprovação automática</span>
                <span className="font-crimson italic text-paper text-sm">— se desligar, pausa pra você aprovar</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input type="checkbox" checked={skipConsensus} onChange={(e) => setSkipConsensus(e.target.checked)} />
                <span className="font-mono text-[13px]">Pular conciliação + voto de Minerva</span>
                <span className="font-crimson italic text-paper text-sm">— se ligar, vai direto pra aprovação</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={enableDiscussion} onChange={(e) => setEnableDiscussion(e.target.checked)} />
                <span className="font-mono text-[13px]">Mesa-redonda 🪑 antes de executar</span>
                <span className="font-crimson italic text-paper text-sm">— agentes debatem o briefing (mais inteligente, +30s)</span>
              </label>
              <button onClick={start}
                className="btn bg-ink text-bone border-ink mt-4 w-full py-3 text-base">
                ▶ INICIAR PIPELINE AUTOMÁTICO
              </button>
            </section>
          )}

          {/* TIMELINE DAS ETAPAS */}
          <section className="border-2 border-ink p-3 bg-bone">
            <div className="font-display font-bold uppercase text-sm mb-3">▸ etapas do pipeline</div>
            <ol className="space-y-1.5">
              {(Object.keys(STEP_LABEL) as PipelineStep[]).map(step => {
                const stepEvents = events.filter(e => e.step === step);
                const last = stepEvents[stepEvents.length - 1];
                const isActive = currentStep === step && running;
                const status = last?.status ?? "idle";
                const color =
                  status === "done"    ? "bg-ink text-bone" :
                  status === "running" ? "bg-gold text-ink animate-pulse" :
                  status === "skipped" ? "bg-graymd text-bone" :
                  status === "error"   ? "bg-red text-bone" :
                  status === "waiting" ? "bg-red text-bone animate-pulse" :
                                         "bg-bone text-graydk border border-ink/20";
                return (
                  <li key={step} className={`flex items-center gap-2 px-2 py-1.5 ${color} ${isActive ? "ring-2 ring-red" : ""}`}>
                    <span className="font-mono text-sm w-5">
                      {status === "idle" ? "·" : STATUS_ICON[status as keyof typeof STATUS_ICON]}
                    </span>
                    <span className="font-display font-bold text-[13px] flex-1">{STEP_LABEL[step]}</span>
                    {last && <span className="font-mono text-[11px] opacity-80 truncate max-w-[40%]">{last.text}</span>}
                  </li>
                );
              })}
            </ol>
          </section>

          {/* AGENTES TRABALHANDO (lúdico) */}
          {assignedActive.length > 0 && (
            <section className="border-2 border-ink p-3 bg-bone">
              <div className="font-display font-bold uppercase text-sm mb-3">▸ time em ação</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {assignedActive.map(p => {
                  const w = workers[p.id];
                  return <WorkerCard key={p.id} persona={p} status={w?.status} detail={w?.detail} />;
                })}
                {/* Marcio + Trump como conciliador / minerva */}
                {(workers["marcio"] || workers["trump"]) && (
                  <>
                    {workers["marcio"] && <WorkerCard persona={PERSONAS.find(p => p.id === "marcio")!}
                                                       status={workers["marcio"].status} detail={workers["marcio"].detail}
                                                       label="Conciliador" />}
                    {workers["trump"] && <WorkerCard persona={PERSONAS.find(p => p.id === "trump")!}
                                                      status={workers["trump"].status} detail={workers["trump"].detail}
                                                      label="Voto de Minerva" />}
                  </>
                )}
              </div>
            </section>
          )}

          {/* MESA-REDONDA visual */}
          {(() => {
            const discussEv = events.find(e => e.step === "discuss" && e.discussion);
            if (!discussEv?.discussion?.length) return null;
            return (
              <section className="border-2 border-ink bg-bone">
                <header className="bg-gold text-ink px-3 py-2 flex items-center justify-between">
                  <span className="font-display font-bold uppercase">🪑 Mesa-Redonda · {discussEv.discussion.length} turnos</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(
                      discussEv.discussion!.map(t => {
                        const p = PERSONAS.find(x => x.id === t.by);
                        return `${p?.name ?? t.by} (${t.kind}): ${t.text}`;
                      }).join("\n")
                    )}
                    className="tag underline">copiar</button>
                </header>
                <div className="p-3 space-y-2 max-h-[50vh] overflow-y-auto">
                  {discussEv.discussion.map((t, i) => {
                    const p = PERSONAS.find(x => x.id === t.by);
                    const kindBadge =
                      t.kind === "question"   ? "bg-graydk text-bone"
                    : t.kind === "objection"  ? "bg-red text-bone"
                    : t.kind === "agreement"  ? "bg-ink text-bone"
                    : t.kind === "synthesis"  ? "bg-gold text-ink"
                    :                            "bg-paper text-bone";
                    const kindLabel =
                      t.kind === "question"   ? "❓ pergunta"
                    : t.kind === "objection"  ? "🚫 objeção"
                    : t.kind === "agreement"  ? "🤝 concorda"
                    : t.kind === "synthesis"  ? "🎯 síntese"
                    :                            "💡 proposta";
                    return (
                      <div key={i} className="flex gap-2 items-start">
                        <div className="w-9 h-9 flex items-center justify-center font-display font-black text-xs shrink-0 overflow-hidden"
                             style={{ background: p ? monogramColor(p.id) : "#5A5A5C", color: "#F2EEE6" }}>
                          {p ? initials(p.name) : "?"}
                        </div>
                        <div className="flex-1 min-w-0 border border-ink bg-bone p-2 relative
                                        before:content-[''] before:absolute before:-left-2 before:top-3 before:w-0 before:h-0
                                        before:border-y-[6px] before:border-y-transparent before:border-r-[8px] before:border-r-ink">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-display font-bold uppercase text-[12px]">{p?.name ?? t.by}</span>
                            <span className={`tag px-1.5 ${kindBadge}`}>{kindLabel}</span>
                          </div>
                          <div className="font-crimson italic text-paper text-[13px] leading-snug">"{t.text}"</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })()}

          {/* LOG DETALHADO */}
          {events.length > 0 && (
            <section className="border-2 border-ink bg-ink/95 text-bone">
              <div className="px-3 py-1.5 border-b border-bone/20 font-mono text-[10px] uppercase">log do pipeline</div>
              <div className="p-3 max-h-48 overflow-y-auto font-mono text-[11px] space-y-0.5">
                {events.map((e, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className={
                      e.status === "done"    ? "text-gold" :
                      e.status === "running" ? "text-bone animate-pulse" :
                      e.status === "skipped" ? "text-graymd" :
                      e.status === "error"   ? "text-red" :
                                               "text-gold"}>
                      {STATUS_ICON[e.status]}
                    </span>
                    <span className="text-bone/60 w-12 shrink-0">{e.step.slice(0, 7)}</span>
                    <span className="flex-1">{e.text}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CHECKPOINT DE APROVAÇÃO */}
          {needsApproval && (
            <section className="border-4 border-red bg-red/10 p-4 animate-pulse">
              <div className="font-display font-black uppercase text-xl mb-2">⚖️ DECISÃO DO MARCIO</div>
              <p className="font-crimson italic text-paper mb-3">
                Pipeline parado aguardando sua decisão final. Veja as entregas na demanda e escolha:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <button onClick={approveAndFinish}
                  className="btn bg-ink text-bone border-ink py-4 text-base">
                  ✓ APROVAR E ENTREGAR
                </button>
                <button onClick={rejectAndStop}
                  className="btn bg-red text-bone border-red py-4 text-base">
                  ✗ REJEITAR (pedir refação)
                </button>
              </div>
            </section>
          )}

          {/* CONTROLES FINAIS */}
          {!running && events.length > 0 && (
            <div className="flex justify-between gap-2">
              <button onClick={() => { setEvents([]); start(); }} disabled={running}
                className="btn btn-fire">↺ rodar de novo</button>
              <button onClick={onClose} className="btn bg-ink text-bone border-ink">fechar</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WorkerCard({ persona, status, detail, label }:
  { persona: typeof PERSONAS[number]; status?: string; detail?: string; label?: string }) {
  const animate =
    status === "thinking" || status === "typing" || status === "reviewing" || status === "drawing";
  const bg =
    status === "delivered" ? "bg-ink text-bone" :
    status === "blocked"   ? "bg-red text-bone" :
    animate                ? "bg-gold text-ink" :
                             "bg-bone text-ink border border-ink/20";
  const icon =
    status === "thinking"  ? "🧠" :
    status === "typing"    ? "✍️" :
    status === "drawing"   ? "🎨" :
    status === "reviewing" ? "👁" :
    status === "delivered" ? "✓" :
    status === "blocked"   ? "✗" :
                             "💤";

  return (
    <div className={`p-2 border-2 border-ink ${bg} ${animate ? "animate-pulse" : ""}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 flex items-center justify-center font-display font-black text-xs shrink-0"
             style={{ background: monogramColor(persona.id), color: "#F2EEE6" }}>
          {initials(persona.name)}
        </div>
        <div className="font-display font-bold uppercase text-[11px] leading-tight flex-1 min-w-0">
          {persona.name}
        </div>
        <span className="text-lg">{icon}</span>
      </div>
      <div className="font-mono text-[9px] truncate">
        {label ? `${label} · ` : ""}{detail ?? status ?? "ocioso"}
      </div>
    </div>
  );
}
